import { Flag } from "lucide-react";
import { toast } from "sonner";

import { useCancelVote } from "../../hooks/useCancelVote";
import { CancelVoteBanner } from "./CancelVoteBanner";
import { CancelVoteButton } from "./CancelVoteButton";

type MatchActionBarProps = {
  matchId: string;
  currentUserId: string;
  players: Array<{ userId: string; username: string | null }>;
  totalPlayers: number;
};

export function MatchActionBar({
  matchId,
  currentUserId,
  players,
  totalPlayers,
}: MatchActionBarProps) {
  const cancelVote = useCancelVote({
    matchId,
    currentUserId,
    totalPlayers,
    enabled: currentUserId.length > 0,
  });

  const handleStartCancelVote = async (input: {
    reason: "server_issue" | "game_bug" | "connection_issue" | "unfair_start" | "other";
    reasonOtherText?: string;
  }) => {
    try {
      await cancelVote.startVote(input);
      toast.success("Cancel vote started", {
        description: `Need ${cancelVote.requiredYesVotes} yes votes to cancel.`,
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to start cancel vote");
    }
  };

  const handleCastCancelVote = async (vote: boolean) => {
    try {
      await cancelVote.castVote(vote);
      toast.success(vote ? "Voted yes to cancel" : "Voted no to continue");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to cast vote");
    }
  };

  const initiatorName =
    players.find((player) => player.userId === cancelVote.activeVote?.initiatorId)?.username ??
    "A player";

  return (
    <div className="space-y-3 rounded-xl border border-surface-700/40 bg-surface-800/40 p-4">
      <div className="flex items-center gap-2">
        <Flag className="h-4 w-4 text-yellow-400" />
        <h3 className="font-display text-sm font-bold uppercase tracking-wide text-surface-100">
          Match Actions
        </h3>
      </div>

      {cancelVote.activeVote ? (
        <CancelVoteBanner
          voteId={cancelVote.activeVote.voteId}
          initiatorName={initiatorName}
          status={cancelVote.activeVote.status}
          reason={cancelVote.activeVote.reason}
          reasonOtherText={cancelVote.activeVote.reasonOtherText}
          yesCount={cancelVote.activeVote.yesCount}
          noCount={cancelVote.activeVote.noCount}
          totalPlayers={cancelVote.activeVote.totalPlayers}
          requiredYesVotes={cancelVote.requiredYesVotes}
          secondsRemaining={cancelVote.secondsRemaining}
          myVote={cancelVote.myVote}
          isCastingVote={cancelVote.isCastingVote}
          onVote={handleCastCancelVote}
        />
      ) : null}

      <CancelVoteButton
        totalPlayers={totalPlayers}
        threshold={cancelVote.threshold}
        canInitiate={cancelVote.canInitiate}
        disabledMessage={cancelVote.disabledMessage}
        votesRemaining={cancelVote.votesRemaining}
        isPending={cancelVote.isStartingVote}
        onStartVote={handleStartCancelVote}
      />
    </div>
  );
}
